import { User } from "oidc-client-ts";

import { authConfig } from "./oidc-config";

type StableAuth = { state: "stable"; owner: string; revision: string; hasUser: boolean };
type TransitionAuth = {
  state: "transitioning";
  transitionId: string;
  previousOwner: string | undefined;
  nextOwner: string;
  nextRevision: string;
  unavailable?: boolean;
};
export type AuthMetadata = StableAuth | TransitionAuth;
export type AuthSnapshot = { metadata: StableAuth; user: User | null };

export const authMetadataKey = `istari.auth-owner:${authConfig.authority}:${authConfig.client_id}`;
export const canonicalUserKey = `oidc.user:${authConfig.authority}:${authConfig.client_id}`;
const listeners = new Set<() => void>();

/** Captures only a stable, canonical authenticated session, never display-profile storage. */
export function captureAuthOwner(): string | undefined {
  const snapshot = readAuthSnapshot();
  return snapshot?.user ? snapshot.metadata.owner : undefined;
}

/** Session identity remains valid while its credentials are being renewed; this never authorizes token use. */
export function getAuthOwnerIdentity(): string | undefined {
  const metadata = readAuthMetadata();
  if (metadata?.state === "transitioning") {
    return !metadata.unavailable && metadata.previousOwner === metadata.nextOwner ? metadata.previousOwner : undefined;
  }
  return captureAuthOwner();
}

export function isAuthOwnerCurrent(owner: string | undefined): boolean {
  return owner !== undefined && getAuthOwnerIdentity() === owner;
}

/** Replacement/removal is synchronous in this tab; cross-tab guards always reread storage. */
export function subscribeAuthOwner(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notifyAuthOwner(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // An application subscriber must not interrupt an auth persistence transaction.
    }
  });
}

export function readAuthMetadata(): AuthMetadata | undefined {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(authMetadataKey) ?? "null");
    if (!raw || typeof raw !== "object" || !("state" in raw)) return undefined;
    if (raw.state === "stable") return parseStable(raw);
    if (raw.state === "transitioning") return parseTransition(raw);
  } catch {
    return undefined;
  }
  return undefined;
}

export function readAuthSnapshot(): AuthSnapshot | undefined {
  try {
    const before = readAuthMetadata();
    if (before?.state !== "stable") return undefined;
    const raw = window.localStorage.getItem(canonicalUserKey);
    const user = raw ? User.fromStorageString(raw) : null;
    const after = readAuthMetadata();
    if (
      after?.state !== "stable" ||
      before.owner !== after.owner ||
      before.revision !== after.revision ||
      before.hasUser !== after.hasUser ||
      before.hasUser !== (user !== null)
    ) {
      return undefined;
    }
    return { metadata: after, user };
  } catch {
    return undefined;
  }
}

export function writeAuthMetadata(metadata: AuthMetadata): void {
  window.localStorage.setItem(authMetadataKey, JSON.stringify(metadata));
}

function parseStable(raw: object): StableAuth | undefined {
  if (!("owner" in raw) || typeof raw.owner !== "string") return undefined;
  if (!("revision" in raw) || typeof raw.revision !== "string") return undefined;
  if (!("hasUser" in raw) || typeof raw.hasUser !== "boolean") return undefined;
  return { state: "stable", owner: raw.owner, revision: raw.revision, hasUser: raw.hasUser };
}

function parseTransition(raw: object): TransitionAuth | undefined {
  if (!("transitionId" in raw) || typeof raw.transitionId !== "string") return undefined;
  if (!("nextOwner" in raw) || typeof raw.nextOwner !== "string") return undefined;
  if (!("nextRevision" in raw) || typeof raw.nextRevision !== "string") return undefined;
  return {
    state: "transitioning",
    transitionId: raw.transitionId,
    nextOwner: raw.nextOwner,
    nextRevision: raw.nextRevision,
    unavailable: "unavailable" in raw && raw.unavailable === true,
    previousOwner: "previousOwner" in raw && typeof raw.previousOwner === "string" ? raw.previousOwner : undefined,
  };
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== authMetadataKey && event.key !== canonicalUserKey && event.key !== null) return;
    const metadata = readAuthMetadata();
    if (metadata?.state === "transitioning" && !metadata.unavailable && metadata.previousOwner === metadata.nextOwner) {
      return;
    }
    notifyAuthOwner();
  });
}
